import FilledButtonM from "@common/components/button/FilledButtonM"
import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import base64 from "@common/utils/base64"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { liveService } from "@data/services/liveService"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { DialogM } from "../common/components/alert/dialog/Dialog"
import { ScreenSkeleton } from "../common/screens/ScreenSkeleton"
import { ChampionImageM } from "../student/champions/ChampionImageM"

const ParentMakeupOccurrencesScreen = (props) => {
    const { route } = props
    const { substitutionId } = route.params
    const studentEmailB64 = route.params?.student ?? ""
    const studentEmail = base64.decode(studentEmailB64.replace(/-/g, ""))
    const [formValues, setFormValues] = useState(null)
    const navigation = useNavigation()

    // Fetch makeup occurrences on mount
    useEffect(() => {
        if (substitutionId && studentEmail) {
            props.apiRequest(
                liveService.getMakeupOccurrences,
                { substitutionId },
                { student_email: studentEmail }
            )
        }
    }, [substitutionId, studentEmail, props.apiRequest])

    const makeupOccurrences = props.makeupOccurrences || []

    const absenceLiveSession = props.absenceLiveSession
    const hasSelection = props.hasSelection
    const selectedOccurrenceId = props.selectedOccurrenceId

    // Transform makeup occurrences into the format expected by the selection component
    const transformedSessions = makeupOccurrences
        .map((occurrence) => ({
            liveSessionId: occurrence.live_session.id,
            occurrenceDate: occurrence.date,
            liveSession: occurrence.live_session,
            occurrence: {
                id: occurrence.id,
                date: occurrence.date,
                started_at: occurrence.started_at,
                ended_at: occurrence.ended_at,
            },
        }))
        .sort((a, b) => {
            const dateA = moment(a.occurrenceDate)
            const dateB = moment(b.occurrenceDate)
            return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
        })

    // Format the absence date for the label
    const absenceDate =
        absenceLiveSession?.next_occurrences?.[0]?.date ||
        absenceLiveSession?.date
    const formattedDate = absenceDate
        ? moment(absenceDate).locale("fr").format("dddd DD MMMM YYYY")
        : ""

    const formData = [
        {
            label: formattedDate
                ? `Séances de rattrapage disponibles pour le live du ${formattedDate}`
                : "Séances de rattrapage disponibles",
            description: (
                <TextM style={{ marginBottom: 10 }} wrap>
                    Le tuteur a proposé les créneaux suivants pour rattraper la
                    séance manquée. Sélectionnez la séance qui convient le mieux
                    à votre enfant.
                </TextM>
            ),
            name: "selected_makeup_occurrence",
            type: "select.single",
            options: transformedSessions.map((session) => {
                const formatted = moment(session.occurrenceDate)
                    .locale("fr")
                    .format("dddd DD MMMM YYYY [à] HH:mm")
                return {
                    label:
                        formatted.charAt(0).toUpperCase() + formatted.slice(1),
                    value: session.occurrence.id,
                }
            }),
            defaultValue: "",
            validationRules: {},
        },
    ]

    const onSubmit = (data) => {
        if (props.selectMakeupOccurrenceRequest) return

        const selectedOccurrenceId = data.selected_makeup_occurrence
        if (!selectedOccurrenceId) return

        props.apiRequest(
            liveService.selectMakeupOccurrence,
            { substitutionId },
            {
                live_occurrence_id: selectedOccurrenceId,
                student_email: studentEmail,
            }
        )
    }

    useApiStatus(
        liveService.selectMakeupOccurrence,
        false,
        false,
        () => {
            DialogM.show({
                text1: "Séance confirmée",
                text2: "Votre enfant a été inscrit à la séance de rattrapage !",
            })
            if (navigation.canGoBack()) {
                navigation.goBack()
            } else {
                navigation.navigate("menu")
            }
        },
        (error) => {
            // If we get a 400 error (session full or already selected), refresh the list
            if (error?.status === 400) {
                DialogM.show({
                    text1: "Séance non disponible",
                    text2:
                        error?.message ||
                        "Cette séance n'est plus disponible. La liste a été mise à jour.",
                })
                // Refresh the makeup occurrences list
                if (substitutionId && studentEmail) {
                    props.apiRequest(
                        liveService.getMakeupOccurrences,
                        { substitutionId },
                        { student_email: studentEmail }
                    )
                }
            }
        }
    )

    // Render content based on state
    const renderContent = () => {
        // If a selection has already been made, show confirmation message
        if (hasSelection) {
            const selectedOccurrence = makeupOccurrences.find(
                (occ) => occ.id === selectedOccurrenceId
            )
            const selectedDate = selectedOccurrence?.date
                ? moment(selectedOccurrence.date)
                      .locale("fr")
                      .format("dddd DD MMMM YYYY [à] HH:mm")
                : ""

            const formattedDate = selectedDate
                ? selectedDate.charAt(0).toUpperCase() + selectedDate.slice(1)
                : ""

            return (
                <View style={{ alignItems: "center", flex: 1, padding: 20 }}>
                    <ChampionImageM name="zoe" variant="happy" width={140} />
                    <TitleM
                        fontWeight="ExtraBold"
                        style={{
                            textAlign: "center",
                            marginTop: 10,
                            color: theme.colors.primary,
                        }}
                        wrap
                    >
                        Séance de rattrapage confirmée
                    </TitleM>
                    <TextM
                        fontWeight="Medium"
                        style={{ textAlign: "center" }}
                        wrap
                    >
                        Une séance de rattrapage a déjà été sélectionnée pour
                        cet élève.
                    </TextM>
                    {formattedDate && (
                        <TextM
                            fontWeight="Bold"
                            style={{ textAlign: "center", marginTop: 10 }}
                            wrap
                        >
                            {formattedDate}
                        </TextM>
                    )}
                    <FilledButtonM
                        onPress={() => {
                            navigation.goBack()
                        }}
                        mode={"text"}
                        label={"Retour"}
                    />
                </View>
            )
        }

        // If no sessions available, show champion with message
        if (transformedSessions.length === 0) {
            return (
                <View style={{ alignItems: "center", flex: 1, padding: 20 }}>
                    <ChampionImageM name="zoe" variant="unhappy" width={140} />
                    <TitleM
                        fontWeight="ExtraBold"
                        style={{
                            textAlign: "center",
                            marginTop: 10,
                            color: theme.colors.primary,
                        }}
                        wrap
                    >
                        Aucune séance disponible
                    </TitleM>
                    <TextM
                        fontWeight="Medium"
                        style={{ textAlign: "center" }}
                        wrap
                    >
                        Toutes les séances proposées sont complètes ou ne sont
                        pas disponibles pour votre enfant.
                    </TextM>
                    <TextM
                        fontWeight="Medium"
                        style={{ textAlign: "center" }}
                        wrap
                    >
                        Veuillez contacter l'équipe Masteur.
                    </TextM>
                    <FilledButtonM
                        onPress={() => {
                            navigation.goBack()
                        }}
                        mode={"text"}
                        label={"Retour"}
                    />
                </View>
            )
        }

        // Show form stepper to select makeup occurrence
        return (
            <View
                style={{
                    flex: 1,
                    alignItems: "center",
                }}
            >
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData.filter((x) => !x.is_masked)}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    defaultValues={{}}
                    onSubmit={onSubmit}
                    submitted={props.selectMakeupOccurrenceRequest}
                    previousRouteName={"menu"}
                    submittedMessage=""
                    serverError={props.selectMakeupOccurrenceError}
                />
            </View>
        )
    }

    return (
        <ScreenSkeleton isLoading={props.getMakeupOccurrencesRequest}>
            {renderContent()}
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        makeupOccurrences: state.data.liveSessions?.makeupOccurrences,
        absenceLiveSession: state.data.liveSessions?.absenceLiveSession,
        hasSelection: state.data.liveSessions?.hasSelection,
        selectedOccurrenceId: state.data.liveSessions?.selectedOccurrenceId,
        getMakeupOccurrencesRequest: state.api.getMakeupOccurrences?.request,
        selectMakeupOccurrenceRequest: state.api.selectMakeupOccurrence?.request,
        selectMakeupOccurrenceError: state.api.selectMakeupOccurrence?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ParentMakeupOccurrencesScreen)
